<script setup>
import { computed, provide, ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import TopBar from './components/TopBar.vue';
import SideNav from './components/SideNav.vue';
import PreviewPanel from './components/PreviewPanel.vue';

// 配置模块组件
import RoleSection from './components/sections/RoleSection.vue';
import KnowledgeSection from './components/sections/KnowledgeSection.vue';
import ScenariosSection from './components/sections/ScenariosSection.vue';
import InboxesSection from './components/sections/InboxesSection.vue';
import HandoffSection from './components/sections/HandoffSection.vue';
import ToolsSection from './components/sections/ToolsSection.vue';
import SettingsSection from './components/sections/SettingsSection.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const assistantId = computed(() => Number(route.params.assistantId));
const activeSection = computed(() => route.params.section || 'role');

const uiFlags = useMapGetter('pilotAssistants/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);

const assistant = computed(() =>
  store.getters['pilotAssistants/getRecord'](assistantId.value)
);

const redirectLegacyLeadsSection = () => {
  if (activeSection.value !== 'leads') {
    return;
  }

  router.replace({
    name: 'pilot_assistant_config_section',
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
      section: 'scenarios',
    },
  });
};

// 保存处理器 - 由各 Section 组件注册
const saveHandler = ref(null);

const registerSaveHandler = handler => {
  saveHandler.value = handler;
};

const executeSave = async () => {
  if (saveHandler.value) {
    try {
      return await saveHandler.value();
    } catch (error) {
      console.error('Save failed:', error);
      return false;
    }
  }
  // 如果当前 section 没有注册 saveHandler，显示提示
  console.warn('No save handler registered for current section');
  return true;
};

// 导航配置
const navItems = computed(() => [
  {
    key: 'role',
    label: t('PILOT.ASSISTANTS.CONFIG.NAV.ROLE'),
    icon: 'i-lucide-smile-plus',
  },
  {
    key: 'knowledge',
    label: t('PILOT.ASSISTANTS.CONFIG.NAV.KNOWLEDGE'),
    icon: 'i-lucide-library-big',
  },
  {
    key: 'scenarios',
    label: t('PILOT.ASSISTANTS.CONFIG.NAV.SCENARIOS'),
    icon: 'i-lucide-git-branch',
  },
  {
    key: 'inboxes',
    label: t('PILOT.ASSISTANTS.CONFIG.NAV.INBOXES'),
    icon: 'i-lucide-inbox',
  },
  {
    key: 'handoff',
    label: t('PILOT.ASSISTANTS.CONFIG.NAV.HANDOFF'),
    icon: 'i-lucide-user-round-plus',
  },
  {
    key: 'tools',
    label: t('PILOT.ASSISTANTS.CONFIG.NAV.TOOLS'),
    icon: 'i-lucide-wrench',
  },
  {
    key: 'settings',
    label: t('PILOT.ASSISTANTS.CONFIG.NAV.SETTINGS'),
    icon: 'i-lucide-settings',
  },
]);

// 根据当前 section 返回对应组件
const currentSection = computed(() => {
  const sections = {
    role: RoleSection,
    knowledge: KnowledgeSection,
    scenarios: ScenariosSection,
    inboxes: InboxesSection,
    handoff: HandoffSection,
    tools: ToolsSection,
    settings: SettingsSection,
  };
  return sections[activeSection.value] || RoleSection;
});

// 导航切换
const handleNavChange = key => {
  router.push({
    name: 'pilot_assistant_config_section',
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
      section: key,
    },
  });
};

// 获取助手数据
const fetchAssistant = async () => {
  if (!assistant.value?.id) {
    await store.dispatch('pilotAssistants/show', { id: assistantId.value });
  }
};

onMounted(() => {
  redirectLegacyLeadsSection();
  fetchAssistant();
});

watch(activeSection, () => {
  redirectLegacyLeadsSection();
});

// 提供给子组件
provide('assistant', assistant);
provide('assistantId', assistantId);
provide('registerSaveHandler', registerSaveHandler);
</script>

<template>
  <div class="flex flex-col w-full h-full">
    <!-- 顶部信息条 -->
    <TopBar
      :assistant="assistant"
      :is-fetching="isFetching"
      @save="executeSave"
    />

    <!-- 主体内容 -->
    <div class="flex flex-1 min-h-0 w-full overflow-hidden">
      <!-- 左侧导航 -->
      <SideNav
        :items="navItems"
        :active-key="activeSection"
        @change="handleNavChange"
      />

      <!-- 中间配置区 -->
      <div
        class="flex-1 overflow-y-auto px-6 bg-n-slate-50 dark:bg-n-slate-900"
      >
        <component :is="currentSection" :assistant="assistant" />
      </div>

      <!-- 右侧预览面板 -->
      <PreviewPanel :assistant="assistant" />
    </div>
  </div>
</template>
